import { useCallback, useRef } from 'react';

/**
 * Tastaturführung für Erfassungsformulare (siehe
 * docs/TASTATURGEFUEHRTE_FAHRTERFASSUNG.md, Abschnitt 3).
 *
 * Ziel: Der Disponent kommt vom ersten Zeichen bis zum Absenden ohne Maus aus.
 * Deshalb liegt die gesamte Logik in EINEM Handler am Container statt verstreut
 * in den einzelnen Feldern – `Strg`+`Enter` muss überall greifen, und die
 * Adressfelder (Google Web Component) liefern ihre Events ohnehin nur gebündelt
 * am Host ab.
 *
 * Felder markieren sich selbst über Attribute, die Reihenfolge ergibt sich aus
 * dem DOM:
 *   data-nav="pickupAddress"      Name des Feldes
 *   data-nav-type="text"          text | multiline | select | place | action
 *   data-nav-open="true"          nur `place`: Vorschlagsliste ist offen
 *   data-nav-cursor-pending="true" nur `place`: Cursor wird gerade gesetzt
 */

// Elemente, die den Fokus tatsächlich annehmen können. `[data-nav]` sitzt bei
// MUI auf der äußeren FormControl, nicht auf dem fokussierbaren Kind.
const FOCUSABLE_SELECTOR =
  'input:not([type="hidden"]), textarea, select, button, [role="combobox"], [tabindex]:not([tabindex="-1"])';

const isMultiline = (type) => type === 'multiline';

/**
 * Kürzel werden über die physische Taste aufgelöst, nicht über das erzeugte
 * Zeichen. Auf macOS belegt Option/Alt die Buchstaben mit Sonderzeichen –
 * ⌥+S liefert `ß`, ⌥+V liefert `√`. Ein Vergleich mit `event.key` findet das
 * Kürzel dort nie, und weil dann auch kein preventDefault läuft, landet das
 * Sonderzeichen im Feld.
 *
 * `event.code` ist layout- und modifierunabhängig: KeyS, KeyV, Digit1, …
 */
const shortcutKeyFromCode = (code) => {
  if (!code) return null;
  if (/^Key[A-Z]$/.test(code)) return code.slice(3).toLowerCase();
  if (/^Digit[0-9]$/.test(code)) return code.slice(5);
  if (/^Numpad[0-9]$/.test(code)) return code.slice(6);
  return null;
};

/**
 * Ermittelt das fokussierbare Element zu einem `[data-nav]`-Knoten.
 * Bei der Places-Komponente liegt das echte <input> im Shadow DOM (der durch
 * den Patch in PlaceAutocompleteElementWrapper offen ist).
 */
/**
 * Gehört Enter gerade einem aufgeklappten Widget?
 *
 * MUI-`Autocomplete` (Kunde, Vorlage, Fahrer, Rechnungskunde …) verarbeitet
 * Enter selbst, solange seine Vorschlagsliste offen ist – genau wie Googles
 * Places-Feld. Statt jede Komponente einzeln zu kennen, wird das ARIA-Attribut
 * ausgewertet, das solche Widgets ohnehin setzen müssen.
 */
const isExpandedWidget = (target) =>
  !!target?.closest?.('[aria-expanded="true"]');

/**
 * Nimmt dieses Element den Fokus wirklich an?
 *
 * `[role="combobox"]` allein genügt nicht: MUI-`Select` setzt die Rolle auf ein
 * fokussierbares `div` mit `tabindex`, MUI-`Autocomplete` dagegen auf den
 * InputBase-Wrapper – dort ist erst das `<input>` darin fokussierbar. Ein
 * `focus()` auf den Wrapper verpufft wirkungslos.
 */
const NATIVELY_FOCUSABLE = ['input', 'textarea', 'select', 'button', 'a'];

const isActuallyFocusable = (element) => {
  if (!element) return false;
  if (element.hasAttribute?.('disabled')) return false;
  if (NATIVELY_FOCUSABLE.includes(element.localName)) return true;
  const tabIndex = element.getAttribute?.('tabindex');
  return tabIndex !== null && tabIndex !== '-1';
};

const resolveFocusable = (element) => {
  if (!element) return null;
  if (element.localName === 'gmp-place-autocomplete') {
    return element.shadowRoot?.querySelector('input') || element;
  }
  if (element.matches?.(FOCUSABLE_SELECTOR) && isActuallyFocusable(element)) return element;

  const candidates = element.querySelectorAll?.(FOCUSABLE_SELECTOR) || [];
  for (const candidate of candidates) {
    if (isActuallyFocusable(candidate)) return candidate;
  }
  // Nichts Fokussierbares – etwa ein deaktiviertes Feld. Der Aufrufer muss
  // weitergehen, sonst endet die Enter-Kette hier tot.
  return null;
};

const useFormKeyboardNav = ({
  // Enter springt im Textfeld zum nächsten Feld. Abschaltbar, weil manche
  // Betriebe Enter-als-Absenden gewohnt sind (settings: keyboardEnterAdvances).
  enterAdvances = true,
  onSubmit,
  // Absenden möglich? Ist der Button deaktiviert, darf Strg+Enter nicht
  // wirkungslos verpuffen.
  canSubmit = true,
  onSubmitBlocked,
  onEscape,
  // F1 – Kurzanleitung. Wer die Belegung vergessen hat, sitzt im Formular und
  // nicht in den Einstellungen.
  onHelp,
  // Zusätzliche Kürzel: { 's': () => …, '1': () => … } – jeweils mit Alt.
  altShortcuts,
} = {}) => {
  const containerRef = useRef(null);
  const altShortcutsRef = useRef(altShortcuts);
  altShortcutsRef.current = altShortcuts;

  const getFields = useCallback(() => {
    if (!containerRef.current) return [];
    return Array.from(containerRef.current.querySelectorAll('[data-nav]'));
  }, []);

  /**
   * Solange ein Adressfeld seinen Cursor positioniert (Hausnummer-Sprung),
   * darf der Fokus nicht wandern – sonst tippt der Disponent die Hausnummer
   * in ein Feld, das er längst verlassen hat.
   */
  const isFocusBlocked = useCallback(
    () => !!containerRef.current?.querySelector('[data-nav-cursor-pending]'),
    []
  );

  const focusElement = useCallback((navElement) => {
    const focusable = resolveFocusable(navElement);
    if (!focusable) return false;

    focusable.focus();

    // Das Panel scrollt; ein per Tastatur angesteuertes Feld kann außerhalb des
    // sichtbaren Bereichs liegen.
    //
    // Adressfelder ausgenommen: Die scrollen sich beim Fokussieren selbst so
    // zurecht, dass unter ihnen Platz für die Vorschlagsliste bleibt
    // (ensureRoomBelow). Ein zusätzliches `nearest` würde das gerade
    // begonnene Zentrieren wieder zurückdrehen.
    if (navElement.dataset?.navType !== 'place') {
      navElement.scrollIntoView?.({ block: 'nearest' });
    }
    return true;
  }, []);

  const focusField = useCallback(
    (name) => {
      if (isFocusBlocked()) return false;
      const target = getFields().find((el) => el.dataset.nav === name);
      return target ? focusElement(target) : false;
    },
    [getFields, focusElement, isFocusBlocked]
  );

  const focusNext = useCallback(
    (fromName) => {
      if (isFocusBlocked()) return false;
      const fields = getFields();
      const index = fields.findIndex((el) => el.dataset.nav === fromName);
      // Deaktivierte Felder überspringen: Sie sind sichtbar (etwa das
      // Vorlagen-Feld ohne ausgewählten Kunden), nehmen aber keinen Fokus an.
      for (let i = index + 1; i < fields.length; i += 1) {
        if (focusElement(fields[i])) return true;
      }
      return false;
    },
    [getFields, focusElement, isFocusBlocked]
  );

  const submit = useCallback(() => {
    if (!canSubmit) {
      onSubmitBlocked?.();
      return;
    }
    onSubmit?.();
  }, [canSubmit, onSubmit, onSubmitBlocked]);

  const handleKeyDown = useCallback(
    (event) => {
      // Das echte Ziel kann im Shadow DOM der Places-Komponente liegen –
      // event.target wäre dann nur der Host. composedPath() liegt am nativen
      // Event; React reicht es am SyntheticEvent nicht durch.
      const nativeEvent = event.nativeEvent || event;
      const path = nativeEvent.composedPath?.() || [];
      const realTarget = path[0]?.nodeType === 1 ? path[0] : event.target;
      const navElement =
        path.find((node) => node?.nodeType === 1 && node.matches?.('[data-nav]')) ||
        // Fallback ohne composedPath: findet alles außerhalb des Shadow DOM.
        event.target?.closest?.('[data-nav]') ||
        null;
      const navType = navElement?.dataset.navType || 'text';

      if (event.key === 'F1') {
        // Sonst öffnet der Browser seine eigene Hilfe.
        event.preventDefault();
        onHelp?.();
        return;
      }

      if (event.key === 'Escape') {
        onEscape?.(event, navElement);
        return;
      }

      if (event.altKey && altShortcutsRef.current) {
        const shortcutKey =
          shortcutKeyFromCode(event.code) || event.key?.toLowerCase() || null;
        const handler = shortcutKey ? altShortcutsRef.current[shortcutKey] : null;
        if (handler) {
          // Ohne preventDefault schreibt macOS das Sonderzeichen ins Feld.
          event.preventDefault();
          handler();
          return;
        }
      }

      if (event.key !== 'Enter') return;

      // Absenden aus jedem Feld heraus.
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        submit();
        return;
      }

      // Buttons behalten ihr natives Verhalten.
      if (navType === 'action') return;

      // Mehrzeilige Notiz: Enter erzeugt einen Zeilenumbruch.
      if (isMultiline(navType)) return;

      // Aufgeklapptes Widget (MUI-Autocomplete) oder Adressfeld mit offener
      // Vorschlagsliste: Enter gehört der Liste, nicht der Navigation. Würden
      // wir hier weiterspringen, käme ein unbestätigter Wert ins Formular.
      //
      // `preventDefault` trotzdem: Das Widget hat seinen Handler zu diesem
      // Zeitpunkt bereits ausgeführt (der Event blubbert von innen nach außen),
      // und im `RideForm` würde der Browser sonst das Formular implizit
      // abschicken – der klassische Places-Bug mit Adresse ohne Koordinaten.
      const widgetOwnsEnter =
        isExpandedWidget(realTarget) ||
        (navType === 'place' && navElement?.dataset.navOpen === 'true');

      if (widgetOwnsEnter) {
        event.preventDefault();
        return;
      }

      // Kein bekanntes Feld: nicht navigieren, aber auch kein implizites
      // Absenden des Formulars zulassen.
      if (!navElement) {
        event.preventDefault();
        return;
      }

      if (!enterAdvances) {
        // Enter darf trotzdem nie das Formular implizit abschicken.
        event.preventDefault();
        return;
      }

      // Alles Übrige: weiter zum nächsten Feld. Bei geschlossenem Select
      // bewusst abweichend von MUI, das hier das Menü öffnen würde – sonst
      // bleibt man an drei Selects hintereinander hängen.
      event.preventDefault();
      focusNext(navElement.dataset.nav);
    },
    [enterAdvances, focusNext, onEscape, onHelp, submit]
  );

  return {
    containerProps: { ref: containerRef, onKeyDown: handleKeyDown },
    focusField,
    focusNext,
    isFocusBlocked,
  };
};

export default useFormKeyboardNav;
